import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Search, X, Eye, Download, Printer, ArrowRight } from 'lucide-react';
import ReactDOM from 'react-dom';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import * as XLSX from 'xlsx';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';
import SortFilterTable from '../ui/SortFilterTable';
import { NEW_SHIPMENT_NOTE_COLUMNS, getNewShipmentNoteCellValue } from '../ui/tableColumnConfigs';
import { GridText, StatusPill } from '../ui/gridCell';

const NewShipment = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [allDeliveryNotes, setAllDeliveryNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [batchSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);

  // Load all delivery notes with CONFIRMED status automatically in batches
  const loadDeliveryNotes = async () => {
    try {
      setLoading(true);
      setError('');

      let allNotes = [];
      let skip = 0;
      let hasMore = true;

      // Build base request data
      const baseRequestData = {
        Status: 'CONFIRMED' // Always filter by CONFIRMED status
      };

      // Load in batches of 25 until all are loaded
      while (hasMore) {
        const requestData = {
          ...baseRequestData,
          Skip: skip,
          Take: batchSize
        };

        const response = await MainService.DeliveryNotesInList(requestData);

        if (response && response.Data) {
          allNotes = [...allNotes, ...response.Data];
          setTotalCount(response.Total || 0);
          
          // Check if we've loaded all notes
          if (response.Data.length < batchSize || allNotes.length >= (response.Total || 0)) {
            hasMore = false;
          } else {
            skip += batchSize;
          }
        } else {
          hasMore = false;
          if (allNotes.length === 0) {
            setError('Failed to load delivery notes');
          }
        }
      }

      setAllDeliveryNotes(allNotes);
    } catch (err) {
      console.error('Error loading delivery notes:', err);
      setError('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDeliveryNotes();
  }, []);

  // Front-end search filtering
  const filteredDeliveryNotes = useMemo(() => {
    if (!searchTerm.trim()) {
      return allDeliveryNotes;
    }

    const keyword = searchTerm.toLowerCase();
    return allDeliveryNotes.filter(note => 
      (note.RefNo || '').toLowerCase().includes(keyword) ||
      (note.Driver?.Name || '').toLowerCase().includes(keyword) ||
      (note.Vehicle?.Number || '').toLowerCase().includes(keyword) ||
      (note.Status || '').toLowerCase().includes(keyword) ||
      (note.Remarks || '').toLowerCase().includes(keyword) ||
      (note.CreatedName || '').toLowerCase().includes(keyword)
    );
  }, [allDeliveryNotes, searchTerm]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const handleView = async (note) => {
    try {
      setLoading(true);
      const response = await MainService.GetDeliveryNoteById(note.Id);
      if (response) {
        setSelectedNote(response);
        setViewModalOpen(true);
      } else {
        setError('Failed to load delivery note details');
      }
    } catch (err) {
      console.error('Error loading delivery note:', err);
      setError('Failed to load delivery note details');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async (note) => {
    try {
      setLoading(true);
      const response = await MainService.PrintDeliveryNoteAllocation(note.Id);
      if (response && response != null) {
        const printUrl = `${getApiBaseUrl()}ReportFiles/${response}`;
        window.open(printUrl, '_blank');
      } else {
        setError('Print Error');
      }
    } catch (err) {
      console.error('Error printing delivery note:', err);
      setError('Print Error');
    } finally {
      setLoading(false);
    }
  };

  const handleConvert = (note) => {
    navigate(`/home/shipments/convert-shipment/${note.Id}`);
  };

  const getNoteCellValue = useCallback(
    (note, field) => getNewShipmentNoteCellValue(note, field, formatDate),
    []
  );

  const renderNoteCell = useCallback((note, column) => {
    switch (column.field) {
      case 'status':
        return <StatusPill name={note.Status} />;
      case 'remarks':
        return <GridText value={note.Remarks} className="max-w-xs" />;
      case 'actions':
        return (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => handleView(note)}
              className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
              title="View Details"
            >
              <Eye className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => handlePrint(note)}
              disabled={loading}
              className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Print"
            >
              <Printer className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => handleConvert(note)}
              disabled={loading}
              className="p-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Convert to Shipment"
            >
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        );
      default:
        return <GridText value={getNewShipmentNoteCellValue(note, column.field, formatDate)} />;
    }
  }, [loading]);

  const exportToExcel = () => {
    if (filteredDeliveryNotes.length === 0) {
      setError('No data to export');
      return;
    }

    try {
      // Prepare data for export
      const exportData = filteredDeliveryNotes.map((note, index) => ({
        'S.No': index + 1,
        'Ref No': note.RefNo || '',
        'Date': formatDate(note.Date),
        'Driver': note.Driver?.Name || '',
        'Vehicle': note.Vehicle ? `${note.Vehicle.Number}${note.Vehicle.Model ? ` (${note.Vehicle.Model})` : ''}` : '',
        'Status': note.Status || '',
        'Remarks': note.Remarks || '',
        'Created By': note.CreatedName || ''
      }));

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Shipments');

      // Set column widths
      const colWidths = [
        { wch: 8 },  // S.No
        { wch: 15 }, // Ref No
        { wch: 12 }, // Date
        { wch: 20 }, // Driver
        { wch: 20 }, // Vehicle
        { wch: 12 }, // Status
        { wch: 30 }, // Remarks
        { wch: 15 }  // Created By
      ];
      ws['!cols'] = colWidths;

      // Generate filename with current date
      const fileName = `Shipments_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Save file
      XLSX.writeFile(wb, fileName);
      setSuccess('Excel file downloaded successfully');
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      setError('Failed to export to Excel');
    }
  };

  const statusColors = {
    'CONFIRMED': 'bg-blue-100 text-blue-800',
    'NEW': 'bg-yellow-100 text-yellow-800',
    'FINISHED': 'bg-green-100 text-green-800',
    'CANCELLED': 'bg-red-100 text-red-800',
    'PENDING': 'bg-yellow-100 text-yellow-800',
    'IN_PROGRESS': 'bg-blue-100 text-blue-800'
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">{getPageHeading(language, 'newShipment')}</h1>
            <p className="text-sm text-gray-500 mt-1">Convert your confirmed delivery notes to shipments</p>
          </div>
          {/* Search Bar */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by Ref No, Driver, Vehicle, Status..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportToExcel}
              disabled={filteredDeliveryNotes.length === 0}
              className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              title="Export Excel"
            >
              <Download className="w-5 h-5" />
            </button>
            <button
              onClick={() => loadDeliveryNotes()}
              disabled={loading}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Toast Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Delivery Notes Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Table Header */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Delivery Notes List (Confirmed)</h2>
            {filteredDeliveryNotes.length > 0 && (
              <span className="text-sm text-gray-600">
                Showing {filteredDeliveryNotes.length} of {totalCount} delivery notes
              </span>
            )}
          </div>
        </div>

        <SortFilterTable
          columns={NEW_SHIPMENT_NOTE_COLUMNS}
          rows={filteredDeliveryNotes}
          getCellValue={getNoteCellValue}
          renderCell={renderNoteCell}
          loading={loading}
          loadingText="Loading delivery notes..."
          emptyText="No confirmed delivery notes found"
          minWidth="1200px"
          maxHeight={600}
          showPagination
        />

      </div>

      {/* View Modal */}
      {viewModalOpen && selectedNote && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-xl font-semibold text-gray-900">Delivery Note Details</h3>
              <button
                onClick={() => {
                  setViewModalOpen(false);
                  setSelectedNote(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ref No</label>
                  <p className="text-sm text-gray-900">{selectedNote.RefNo || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <p className="text-sm text-gray-900">{formatDate(selectedNote.Date)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Driver</label>
                  <p className="text-sm text-gray-900">{selectedNote.Driver?.Name || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle</label>
                  <p className="text-sm text-gray-900">
                    {selectedNote.Vehicle ? `${selectedNote.Vehicle.Number}${selectedNote.Vehicle.Model ? ` (${selectedNote.Vehicle.Model})` : ''}` : '-'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${
                    statusColors[selectedNote.Status] || 'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedNote.Status || '-'}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Created By</label>
                  <p className="text-sm text-gray-900">{selectedNote.CreatedName || '-'}</p>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                  <p className="text-sm text-gray-900">{selectedNote.Remarks || '-'}</p>
                </div>
              </div>
              {selectedNote.DeliveryNoteDetails && selectedNote.DeliveryNoteDetails.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Orders ({selectedNote.DeliveryNoteDetails.length})</h4>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left font-medium text-gray-700">Voucher No</th>
                          <th className="px-4 py-2 text-left font-medium text-gray-700">Customer</th>
                          <th className="px-4 py-2 text-left font-medium text-gray-700">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedNote.DeliveryNoteDetails.map((detail) => (
                          <tr key={detail.Id}>
                            <td className="px-4 py-2 text-gray-600">{detail.Order?.VoucherNo || '-'}</td>
                            <td className="px-4 py-2 text-gray-600">{detail.Order?.Customer?.Name || '-'}</td>
                            <td className="px-4 py-2 text-gray-600">
                              {detail.Order?.CustomerAmount !== null && detail.Order?.CustomerAmount !== undefined 
                                ? `AED ${parseFloat(detail.Order.CustomerAmount).toFixed(2)}` 
                                : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
              <button
                onClick={() => {
                  setViewModalOpen(false);
                  setSelectedNote(null);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
};

export default NewShipment;

